import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowRight } from '../ui/ProjectIcons';

/** Only the border carries the verdict, with no fill, like the Shared account
 *  notice; the text stays black either way.
 *  `null` = a response with no pass/fail meaning — neutral, not a false green. */
function testResponseClasses(isSuccess: boolean | null): string {
  if (isSuccess === null)
    return 'border-2 border-ui-divider-lines text-ui-text-primary-text';
  return isSuccess
    ? 'border-2 border-teal-t40 text-ui-text-primary-text'
    : 'border-2 border-ui-status-error-destructive text-ui-text-primary-text';
}

/** For a JSON payload. It sits inside the verdict box, so it takes a plain
 *  fill rather than a second verdict border. */
export const testResponsePreClasses =
  'bg-greyscale-11 text-ui-text-primary-text p-2 rounded text-body-sm w-full overflow-auto max-h-60';

/** For a plain-language message (a cause, an account), not a JSON payload,
 *  so it reads as text rather than code. */
export function testResponseMessageClasses(isSuccess: boolean | null): string {
  return `${testResponseClasses(isSuccess)} px-4 py-3 rounded text-body-sm w-full whitespace-pre-wrap break-words`;
}

/** A trigger label that swaps to its busy text without resizing the button:
 *  both texts share one grid cell, so the button keeps the wider one's width. */
export const TestActionButtonLabel: React.FC<{
  label: React.ReactNode;
  busyLabel: React.ReactNode;
  isBusy: boolean;
}> = ({ label, busyLabel, isBusy }) => (
  <span className="inline-grid">
    <span
      className={`col-start-1 row-start-1 ${isBusy ? 'invisible' : ''}`}
      aria-hidden={isBusy}
    >
      {label}
    </span>
    <span
      className={`col-start-1 row-start-1 ${isBusy ? '' : 'invisible'}`}
      aria-hidden={!isBusy}
    >
      {busyLabel}
    </span>
  </span>
);

export type TestActionVerdict = {
  message: string;
  /** true → green, false → orange, null → neutral. */
  isSuccess: boolean | null;
  /** A second plain-language line under the message, such as the account
   *  the credentials belong to or why the check failed. */
  note?: string;
  /** The payload, shown under the message box. */
  details?: React.ReactNode;
};

type TestActionLayoutProps = {
  formTitle?: React.ReactNode;
  /** The trigger. Each flow owns its own gating, label and tooltips. */
  button: React.ReactNode;
  isLoading: boolean;
  verdict: TestActionVerdict | null;
  /** Derives `${testIdPrefix}-layout`, `-title-row`, `-result`,
   *  `-details-toggle`, `-details`, `-box`, `-message`, `-note`. */
  testIdPrefix?: string;
};

/** Shared chrome for "run a test action and show what came back". Both the
 *  saved-integration flow (TestAction) and the draft one
 *  (DraftTestActionButton) render through this so they can't drift apart —
 *  only the trigger and the details body differ between them. */
export const TestActionLayout: React.FC<TestActionLayoutProps> = ({
  formTitle,
  button,
  isLoading,
  verdict,
  testIdPrefix,
}) => {
  const { t } = useTranslation();
  const [isDetailVisible, setDetailVisible] = useState(true);
  // The result only shows inside View Details, so a new run reopens it;
  // otherwise a panel collapsed earlier would hide the new result.
  useEffect(() => {
    if (isLoading) setDetailVisible(true);
  }, [isLoading]);

  const showVerdict = !isLoading && !!verdict;
  const note = verdict?.note?.trim();

  return (
    <div
      className="flex w-full flex-col"
      data-testid={testIdPrefix ? `${testIdPrefix}-layout` : undefined}
    >
      <div
        className="flex w-full flex-row items-end justify-between"
        data-testid={testIdPrefix ? `${testIdPrefix}-title-row` : undefined}
      >
        <div className="flex flex-col items-start">
          <div className="text-heading-md font-normal text-ui-text-primary-text transition-all duration-300">
            {formTitle}
          </div>
          {showVerdict && (
            // -ml-1.5 cancels the empty space inside the 20px arrow icon, so the
            // arrow lines up with the result box's edge. Change both together.
            <button
              type="button"
              className="-ml-1.5 flex items-center text-body-sm text-ui-text-primary-text hover:text-ui-cta-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-t100 focus-visible:ring-offset-2"
              onClick={() => setDetailVisible(!isDetailVisible)}
              aria-expanded={isDetailVisible}
              data-testid={
                testIdPrefix ? `${testIdPrefix}-details-toggle` : undefined
              }
            >
              {isDetailVisible ? (
                <ArrowDown size={20} />
              ) : (
                <ArrowRight size={20} />
              )}
              {t('View Details')}
            </button>
          )}
        </div>
        <div className="flex flex-row items-center">
          {button}
          {/* Always mounted: a live region has to exist before its
              content changes to be announced. Visually the result lives
              under View Details, which can be collapsed. */}
          <div
            role="status"
            aria-live="polite"
            className="sr-only"
            data-testid={testIdPrefix ? `${testIdPrefix}-result` : undefined}
          >
            {showVerdict && verdict.message}
          </div>
        </div>
      </div>

      {showVerdict && isDetailVisible && (
        <div
          className="mt-3 flex w-full flex-col gap-2"
          data-testid={testIdPrefix ? `${testIdPrefix}-details` : undefined}
        >
          <div
            className={testResponseMessageClasses(verdict.isSuccess)}
            data-testid={testIdPrefix ? `${testIdPrefix}-box` : undefined}
          >
            <p
              data-testid={testIdPrefix ? `${testIdPrefix}-message` : undefined}
            >
              {verdict.message}
            </p>
            {note && (
              <p
                data-testid={testIdPrefix ? `${testIdPrefix}-note` : undefined}
              >
                {note}
              </p>
            )}
            {/* Inside the box, so its border frames the whole result. The
                tables keep their own text size, not the message's. */}
            {verdict.details && (
              <div className="mt-3 flex flex-col gap-8 whitespace-normal text-body-md">
                {verdict.details}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestActionLayout;
